import { CheckIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useClipboard } from "use-clipboard-copy";

import { useProfileContext } from "src/shared/contexts/ProfileContext";
import { useGetFile } from "src/shared/hooks";
import { Nullable } from "src/shared/utils/typescript";
import { ClockIcon } from "src/static/icons";
import {
  HackerDirectoryDiscordIcon,
  HackerDirectoryLinkedInIcon,
  HackerDirectoryProfilePlaceholder,
  HackerDirectoryTeammateUserGrayIcon,
  HackerDirectoryTeammateUserWhiteIcon,
  HackerDirectoryXIcon,
} from "src/static/img";
import SlackAccountLinkButton from "src/views/profile/components/SlackAccountLinkButton";

import SlackConnectButton from "../SlackConnectButton";
import { DiscoveryProfile } from "../types";
import { getTeammateRelationship } from "../utils";

const hasItems = (items: Nullable<string[]>): items is string[] =>
  Boolean(items && items.length > 0);

export interface ProfileIdentityBreakpoints {
  sideBySide: Array<{
    minWidth: number;
    maxWidth: number;
  }>;
}

const ProfileIdentity: React.FC<{
  profile: DiscoveryProfile;
  title: string;
  breakpoints: ProfileIdentityBreakpoints;
  connectSlack?: boolean;
  linkSlackAccount?: boolean;
}> = ({ profile, title, breakpoints, connectSlack, linkSlackAccount }) => {
  const clipboard = useClipboard();
  const [isDiscordCopied, setIsDiscordCopied] = useState(false);
  const discordCopiedTimeoutRef = useRef<Nullable<number>>(null);
  const photo = useGetFile(profile.profilePhoto);
  const { profile: authenticatedProfile } = useProfileContext();
  const relationship = getTeammateRelationship({
    authenticatedClaimId: authenticatedProfile?.claimId ?? null,
    authenticatedTeammateClaimIds:
      authenticatedProfile?.teammates_claim_ids ?? null,
    targetClaimId: profile.id,
    targetTeammateClaimIds: profile.teammatesClaimIds,
  });
  const relationshipCopy =
    relationship === "mutual"
      ? "Connected! You've both added each other."
      : relationship === "pending"
      ? "You added this hacker as a teammate. Waiting for them to add you back."
      : null;

  useEffect(
    () => () => {
      if (discordCopiedTimeoutRef.current != null) {
        window.clearTimeout(discordCopiedTimeoutRef.current);
      }
    },
    []
  );

  const socialContacts = [
    { label: "LinkedIn", value: profile.contactLinkedin },
    { label: "X", value: profile.contactX },
  ] as const;
  const validSocialContacts = socialContacts
    .map((contact) => {
      let externalUrl: Nullable<string>;

      if (contact.value) {
        const candidate = /^https?:\/\//i.test(contact.value)
          ? contact.value
          : `https://${contact.value}`;

        try {
          const url = new URL(candidate);
          if (url.protocol === "http:" || url.protocol === "https:") {
            externalUrl = url.toString();
          }
        } catch {
          externalUrl = undefined;
        }
      }

      return { ...contact, href: externalUrl };
    })
    .filter((contact) => Boolean(contact.href));
  const linkedin = validSocialContacts.find(
    (contact) => contact.label === "LinkedIn"
  );
  const x = validSocialContacts.find((contact) => contact.label === "X");

  const copyDiscordHandle = () => {
    if (!profile.contactDiscord) return;

    clipboard.copy(profile.contactDiscord);
    setIsDiscordCopied(true);
    if (discordCopiedTimeoutRef.current != null) {
      window.clearTimeout(discordCopiedTimeoutRef.current);
    }
    discordCopiedTimeoutRef.current = window.setTimeout(
      () => setIsDiscordCopied(false),
      2000
    );
  };

  return (
    <Identity $breakpoints={breakpoints}>
      <PhotoFrame $breakpoints={breakpoints}>
        <PhotoArea>
          {photo?.uri ? (
            <Photo src={photo.uri} alt={`${title}'s profile photo`} />
          ) : (
            <PhotoPlaceholder
              src={HackerDirectoryProfilePlaceholder}
              alt=""
              aria-hidden
            />
          )}
        </PhotoArea>
      </PhotoFrame>
      <IdentityDetails $breakpoints={breakpoints}>
        <NameRow>
          <Name>{title}</Name>
          {relationshipCopy ? (
            <StatusBadge
              $variant={relationship === "mutual" ? "mutual" : "pending"}
              aria-label={relationshipCopy}
            >
              {relationship === "mutual" ? "Teammate" : "Pending"}
              {relationship === "mutual" ? (
                <CheckIcon aria-hidden width={14} height={14} />
              ) : (
                <ClockIcon aria-hidden width={14} height={14} />
              )}
              <StatusTooltip role="tooltip">{relationshipCopy}</StatusTooltip>
            </StatusBadge>
          ) : (
            profile.teammatesNeeded != null && (
              <StatusBadge
                $variant={profile.teammatesNeeded > 0 ? "looking" : "idle"}
              >
                Looking for {profile.teammatesNeeded}
                <LookingForIconFrame>
                  <LookingForIcon
                    src={
                      profile.teammatesNeeded > 0
                        ? HackerDirectoryTeammateUserWhiteIcon
                        : HackerDirectoryTeammateUserGrayIcon
                    }
                    alt=""
                    aria-hidden
                  />
                </LookingForIconFrame>
              </StatusBadge>
            )
          )}
        </NameRow>

        {(hasItems(profile.pronouns) ||
          linkedin ||
          profile.contactDiscord ||
          x) && (
          <ProfileMetaRow>
            {hasItems(profile.pronouns) ? (
              <Metadata>{profile.pronouns.join(" · ")}</Metadata>
            ) : (
              <span />
            )}
            <Socials>
              {linkedin && (
                <SocialLink
                  href={linkedin.href as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open LinkedIn profile"
                >
                  <LinkedInIcon src={HackerDirectoryLinkedInIcon} alt="" />
                </SocialLink>
              )}
              {profile.contactDiscord && (
                <DiscordButton
                  type="button"
                  aria-label={`Copy Discord handle ${profile.contactDiscord}`}
                  aria-describedby={`discord-tooltip-${profile.id}`}
                  onClick={copyDiscordHandle}
                >
                  <DiscordIcon src={HackerDirectoryDiscordIcon} alt="" />
                  <DiscordTooltip
                    id={`discord-tooltip-${profile.id}`}
                    role="tooltip"
                    aria-live="polite"
                  >
                    {isDiscordCopied ? "Copied!" : profile.contactDiscord}
                  </DiscordTooltip>
                </DiscordButton>
              )}
              {x && (
                <SocialLink
                  href={x.href as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open X profile"
                >
                  <XIcon src={HackerDirectoryXIcon} alt="" />
                </SocialLink>
              )}
            </Socials>
          </ProfileMetaRow>
        )}
        {linkSlackAccount && <SlackAccountLinkButton />}
        {connectSlack && <SlackConnectButton claimId={profile.id} />}
      </IdentityDetails>
    </Identity>
  );
};

const Identity = styled.div<{ $breakpoints: ProfileIdentityBreakpoints }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ $breakpoints }) =>
    $breakpoints.sideBySide.map(
      ({ minWidth, maxWidth }) => css`
        @media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px) {
          flex-direction: row;
          align-items: center;
          gap: 24px;
        }
      `
    )}
`;

const IdentityDetails = styled.div<{
  $breakpoints: ProfileIdentityBreakpoints;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ $breakpoints }) =>
    $breakpoints.sideBySide.map(
      ({ minWidth, maxWidth }) => css`
        @media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px) {
          min-width: 0;
          flex: 1;
        }
      `
    )}
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Name = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 26px;
  line-height: 1;
`;

const StatusTooltip = styled.span`
  position: absolute;
  z-index: 3;
  bottom: calc(100% + 8px);
  left: 50%;
  width: 186px;
  padding: 8px 12px;
  transform: translateX(-50%) translateY(2px);
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  visibility: hidden;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, transform 120ms ease, visibility 120ms ease;

  &::before,
  &::after {
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    transform: translateX(-50%);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
  }

  &::before {
    border-top: 7px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &::after {
    margin-top: -1px;
    border-top: 6px solid ${({ theme }) => theme.globalConstants.color.white};
  }
`;

const StatusBadge = styled.span<{
  $variant: "looking" | "idle" | "mutual" | "pending";
}>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: 2px;
  border-radius: 27.568px;
  background: ${({ $variant, theme }) =>
    ({
      looking: theme.globalConstants.color.backgroundPrimary,
      idle: theme.globalConstants.color.backgroundGrayDark,
      mutual: theme.globalConstants.color.backgroundSuccess,
      pending: "#F59E0B",
    }[$variant])};
  color: ${({ $variant, theme }) =>
    $variant === "idle"
      ? theme.globalConstants.color.textSecondary
      : theme.globalConstants.color.white};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  &:hover > [role="tooltip"],
  &:focus-within > [role="tooltip"] {
    transform: translateX(-50%) translateY(0);
    visibility: visible;
    opacity: 1;
  }
`;
const LookingForIconFrame = styled.span`
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const LookingForIcon = styled.img`
  width: 9.8px;
  height: 10.5px;
`;

const PhotoFrame = styled.figure<{
  $breakpoints: ProfileIdentityBreakpoints;
}>`
  display: flex;
  width: 100%;
  margin: 0;
  flex-direction: column;
  gap: 8px;

  ${({ $breakpoints }) =>
    $breakpoints.sideBySide.map(
      ({ minWidth, maxWidth }) => css`
        @media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px) {
          width: 200px;
          flex: 0 0 200px;
        }
      `
    )}
`;

const PhotoArea = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  background: #eff8fa;
  aspect-ratio: 1;
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PhotoPlaceholder = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Metadata = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 16px;
  line-height: 1.5;
`;

const ProfileMetaRow = styled.div`
  display: flex;
  min-height: 24px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Socials = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 9px;
`;

const SocialLink = styled.a`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-decoration: none;

  &:focus-visible {
    border-radius: 4px;
    outline: 3px solid ${({ theme }) => theme.globalConstants.color.focus};
  }
`;

const DiscordTooltip = styled.span`
  position: absolute;
  z-index: 2;
  top: calc(100% + 8px);
  left: 50%;
  padding: 6px 10px;
  transform: translate(-50%, -2px);
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  visibility: hidden;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, transform 120ms ease, visibility 120ms ease;
  white-space: nowrap;

  &::before,
  &::after {
    position: absolute;
    left: 50%;
    width: 0;
    height: 0;
    transform: translateX(-50%);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
  }

  &::before {
    top: -7px;
    border-bottom: 7px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &::after {
    top: -6px;
    border-bottom: 7px solid ${({ theme }) => theme.globalConstants.color.white};
  }
`;

const DiscordButton = styled.button`
  position: relative;
  display: inline-flex;
  width: 24px;
  height: 24px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;

  &:hover ${DiscordTooltip} {
    transform: translate(-50%, 0);
    visibility: visible;
    opacity: 1;
  }

  &:focus ${DiscordTooltip} {
    transform: translate(-50%, 0);
    visibility: visible;
    opacity: 1;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px
      ${({ theme }) => theme.globalConstants.color.backgroundPrimaryLight};
    outline: none;
  }
`;

const LinkedInIcon = styled.img`
  width: 17.987px;
  height: 17.987px;
`;

const DiscordIcon = styled.img`
  width: 20.999px;
  height: 16.004px;
`;

const XIcon = styled.img`
  width: 17.965px;
  height: 16.238px;
`;

export default ProfileIdentity;
